// The block model every markdown page compiles into, and the one HTML renderer for it.
//
// Both the crawlable server snapshot and the React client inject the same string, so
// what a crawler reads and what a visitor sees cannot drift apart. The input is our
// own build-time content, and everything is escaped here before it becomes markup.

export type Block =
  | { type: 'paragraph'; text: string }
  | { type: 'subheading'; text: string }
  | { type: 'steps'; items: string[] }
  | { type: 'bullets'; items: string[] }
  | { type: 'facts'; rows: Array<[label: string, value: string]> }
  | { type: 'table'; head: string[]; rows: string[][] }
  | { type: 'code'; text: string; lang?: string }
  | { type: 'note'; text: string }
  | { type: 'stores' };

export type Section = { id: string; title: string; blocks: Block[] };

/** Store listings from env. A store without a URL is not approved yet and is left out. */
export type StoreLinks = { chrome?: string; edge?: string; firefox?: string };

export const escapeHtml = (value: string) => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;');

// Only site-relative paths and https URLs become links; anything else stays text, so
// a typo in the markdown can never produce a javascript: or protocol-relative href.
export const safeHref = (href: string) => /^\/(?!\/)/.test(href) || /^https:\/\//.test(href);

const INLINE = /`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;

/** `code` and [text](href) inside running text. Everything else is escaped. */
export function inlineHtml(text: string): string {
  let html = '';
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    html += escapeHtml(text.slice(last, match.index));
    const [whole, code, label, href] = match;
    if (code !== undefined) html += `<code>${escapeHtml(code)}</code>`;
    else if (safeHref(href)) {
      const external = href.startsWith('https://');
      html += `<a href="${escapeHtml(href)}"${external ? ' rel="noopener"' : ''}>${escapeHtml(label)}</a>`;
    } else html += escapeHtml(whole);
    last = match.index + whole.length;
  }
  return html + escapeHtml(text.slice(last));
}

/** Internal paths linked from running text, for the tests that keep links unbroken. */
export function inlineLinks(text: string): string[] {
  return [...text.matchAll(INLINE)].map((match) => match[3]).filter((href): href is string => Boolean(href));
}

export const STORE_ORDER = ['chrome', 'edge', 'firefox'] as const;
export type Store = typeof STORE_ORDER[number];
export const STORE_NAMES: Record<Store, string> = { chrome: 'Chrome Web Store', edge: 'Edge Add-ons', firefox: 'Firefox Add-ons' };
const STORE_CARDS: Record<Store, { browser: string; cta: string; detail: string }> = {
  chrome: { browser: 'Chrome', cta: 'Install for Chrome', detail: 'Also Brave, Vivaldi and Opera. Web Push alerts and a side panel.' },
  edge: { browser: 'Microsoft Edge', cta: 'Install for Edge', detail: 'The same build as Chrome, published for Edge. Web Push alerts and a side panel.' },
  firefox: { browser: 'Firefox', cta: 'Add to Firefox', detail: 'Firefox 140+ on desktop and 142+ on Android. Checks for mail every minute while closed.' },
};

export const storeIcon = (store: Store) =>
  `<img class="store-icon" src="/${store}.svg" alt="" width="20" height="20" aria-hidden="true" />`;

// data-track is read by the client's delegated click handler, so these links are
// counted exactly like the React store buttons without being React elements.
function storeCardsHtml(links: StoreLinks): string {
  const cards = STORE_ORDER.filter((store) => links[store]).map((store) => {
    const card = STORE_CARDS[store];
    return `<a class="store-card" href="${escapeHtml(links[store]!)}" data-track="${store}_click" data-placement="store_row">${storeIcon(store)}<b>${card.browser}</b><span>${escapeHtml(card.detail)}</span><em>${card.cta} ↗</em></a>`;
  }).join('');
  return cards ? `<div class="store-cards">${cards}</div>` : '';
}

function blockHtml(block: Block, links: StoreLinks): string {
  switch (block.type) {
    case 'paragraph': return `<p>${inlineHtml(block.text)}</p>`;
    case 'subheading': return `<h3>${inlineHtml(block.text)}</h3>`;
    case 'steps': return `<ol>${block.items.map((item) => `<li>${inlineHtml(item)}</li>`).join('')}</ol>`;
    case 'bullets': return `<ul>${block.items.map((item) => `<li>${inlineHtml(item)}</li>`).join('')}</ul>`;
    case 'facts': return `<dl>${block.rows.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd><code>${escapeHtml(value)}</code></dd></div>`).join('')}</dl>`;
    case 'table': return `<div class="table-scroll"><table><thead><tr>${block.head.map((cell) => `<th scope="col">${inlineHtml(cell)}</th>`).join('')}</tr></thead><tbody>${block.rows.map((row) => `<tr>${row.map((cell, index) => index === 0 ? `<th scope="row">${inlineHtml(cell)}</th>` : `<td>${inlineHtml(cell)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`;
    case 'code': return `<pre${block.lang ? ` data-lang="${escapeHtml(block.lang)}"` : ''}><code>${escapeHtml(block.text)}</code></pre>`;
    case 'note': return `<div class="docs-note"><b>Note</b><p>${inlineHtml(block.text)}</p></div>`;
    case 'stores': return storeCardsHtml(links);
  }
}

export function sectionsHtml(sections: Section[], links: StoreLinks = {}): string {
  return sections.map((section) =>
    `<section id="${escapeHtml(section.id)}"><h2>${escapeHtml(section.title)}</h2>${section.blocks.map((block) => blockHtml(block, links)).join('')}</section>`,
  ).join('');
}
